import { useState, useCallback, useMemo } from 'react';
import { productApprovalsApi } from '../data/productApprovalsApi';
import { ApprovalOperation } from '../types/ApprovalOperation';
import { genericErrorAlert } from '../utils/alertUtils';
import { Metrics } from '../utils/metrics';
import { captureException, reportRequestException } from '../utils/sentryUtils';
import { useGetLatestApprovalQuery } from '../../__generated__/chirp/com/hubspot/product/approvals/rpc/ApprovalsRpc.hooks';
import { useAppHubUsers } from './useAppHubUsers';
import { useGetIsUngated } from './useGetIsUngated';
import { GATES } from '../constants/auth';
import { chirpApprovalToApprovalData } from '../utils/chirpApprovalAdapters';
import { useChirpCreateApproval } from './useChirpCreateApproval';
import { useChirpApproveApproval } from './useChirpApproveApproval';
import { useChirpRejectApproval } from './useChirpRejectApproval';
import { useChirpCancelApproval } from './useChirpCancelApproval';
import { useChirpUpdateApproval } from './useChirpUpdateApproval';
const reportMetric = approvalOperation => {
  switch (approvalOperation) {
    case ApprovalOperation.REQUEST:
      Metrics.counter('request-created').increment();
      break;
    case ApprovalOperation.APPROVE:
      Metrics.counter('request-approved').increment();
      break;
    case ApprovalOperation.CANCEL:
      Metrics.counter('request-recalled').increment();
      break;
    default:
      return;
  }
};
export const useApprovalData = (approvalType, approvalAction, approvableObjectId, assetName, approvalObjectIds) => {
  const getIsUngated = useGetIsUngated();
  const isChirpEnabled = getIsUngated(GATES.CHIRP_MIGRATION);
  const [isLoading, setIsLoading] = useState(false);
  const [data, setData] = useState(null);
  const [fetchError, setFetchError] = useState();

  // ========= REST path =========
  const fetchLatest = useCallback(() => {
    if (isChirpEnabled) return;
    setIsLoading(true);
    productApprovalsApi.fetchLatestApproval({
      approvalAction,
      approvalType,
      approvableObjectId: approvableObjectId
    }).then(approvalLatest => {
      setData(approvalLatest.approval);
    }).catch(errorResponse => {
      const errorReason = errorResponse.responseJSON && errorResponse.responseJSON.subCategory;
      setFetchError(errorReason || 'UNKNOWN_SUBCATEGORY');
      genericErrorAlert();
      reportRequestException('fetchLatestApproval', errorResponse);
    }).finally(() => {
      setIsLoading(false);
    }).catch(() => {
      console.error('Error fetching approval data');
    });
  }, [approvalAction, approvalType, approvableObjectId, isChirpEnabled]);

  // ========= CHIRP path =========
  const {
    data: chirpData,
    loading: chirpLoading,
    refetch: chirpRefetch
  } = useGetLatestApprovalQuery({
    variables: {
      approvalType,
      approvalAction,
      approvableObjectId: approvableObjectId
    },
    skip: !isChirpEnabled || !approvableObjectId,
    /**
     * fetchPolicy 'network-only' ensures fresh data.
     * We cannot remove this as we always need to fetch the latest approval.
     */
    fetchPolicy: 'network-only',
    onError: chirpError => {
      var _networkError;
      const errorType = (chirpError === null || chirpError === void 0 || (_networkError = chirpError.networkError) === null || _networkError === void 0 || (_networkError = _networkError.cause) === null || _networkError === void 0 ? void 0 : _networkError.errorType) || 'UNKNOWN_SUBCATEGORY';
      setFetchError(errorType);
      genericErrorAlert();
      captureException(chirpError, {
        extra: {
          fingerprint: 'useApprovalData-fetchLatest-chirp',
          approvalType,
          approvalAction,
          approvableObjectId
        }
      });
    }
  });
  const actorUserIds = useMemo(() => {
    const chirpApproval = chirpData === null || chirpData === void 0 ? void 0 : chirpData.approval;
    if (!chirpApproval) return [];
    const ids = [Number(chirpApproval.requester.id)];
    for (const step of (_chirpApproval$approv = chirpApproval.approvalSteps) !== null && _chirpApproval$approv !== void 0 ? _chirpApproval$approv : []) {
      var _chirpApproval$approv;
      ids.push(Number(step.approver.id));
    }
    return [...new Set(ids)];
  }, [chirpData]);
  const {
    usersRecord: approverUsers,
    loading: approverUsersLoading,
    error: fetchApproverUsersError
  } = useAppHubUsers(isChirpEnabled ? actorUserIds : []);
  const chirpDerivedData = useMemo(() => {
    const chirpApproval = chirpData === null || chirpData === void 0 ? void 0 : chirpData.approval;
    if (!chirpApproval || approverUsersLoading) return null;
    return chirpApprovalToApprovalData(chirpApproval, approverUsers, approvalType, approvalAction);
  }, [chirpData, approverUsers, approverUsersLoading, approvalType, approvalAction]);
  const resolvedData = useMemo(() => isChirpEnabled ? chirpDerivedData : data, [isChirpEnabled, chirpDerivedData, data]);
  const chirpFetchLatest = useCallback(() => {
    void chirpRefetch();
  }, [chirpRefetch]);

  // ========= CHIRP mutation hooks =========
  const chirpCreate = useChirpCreateApproval({
    approvalType,
    approvalAction,
    approvableObjectId,
    assetName,
    approvalObjectIds,
    usersRecord: approverUsers
  });
  const chirpApprove = useChirpApproveApproval({
    approvalType,
    approvalAction,
    approvableObjectId,
    usersRecord: approverUsers,
    resolvedData
  });
  const chirpReject = useChirpRejectApproval({
    approvalType,
    approvalAction,
    approvableObjectId,
    usersRecord: approverUsers,
    resolvedData
  });
  const chirpCancel = useChirpCancelApproval({
    approvalType,
    approvalAction,
    approvableObjectId,
    usersRecord: approverUsers,
    resolvedData
  });
  const chirpUpdate = useChirpUpdateApproval({
    approvalType,
    approvalAction,
    approvableObjectId,
    usersRecord: approverUsers,
    resolvedData
  });

  // ========= REST mutation handler =========
  const handleApiPromise = useCallback((approvalOperation, apiPromise) => apiPromise.then(response => {
    if (response && 'id' in response) {
      setData(response);
    }
    reportMetric(approvalOperation);
    return response;
  }).catch(errorResponse => {
    reportRequestException(approvalOperation, errorResponse);
    throw errorResponse;
  }), []);

  // ========= create =========
  const create = useCallback((note, approverOwnerIds, approvalRules, dueDate, title) => {
    if (isChirpEnabled) {
      return chirpCreate(note, approverOwnerIds, approvalRules, dueDate, title);
    }
    const basePayload = Object.assign({
      title: title !== null && title !== void 0 ? title : assetName,
      approvalType,
      approvalAction,
      approvalRules,
      note,
      approverOwnerIds
    }, dueDate && {
      dueDate
    });
    if (approvalObjectIds) {
      const payload = approvalObjectIds.map(id => Object.assign({}, basePayload, {
        approvableObjectId: id
      }));
      const promise = productApprovalsApi.createBulkApproval(payload);
      return handleApiPromise(ApprovalOperation.REQUEST, promise);
    } else {
      const payload = Object.assign({}, basePayload, {
        approvableObjectId: approvableObjectId
      });
      const promise = productApprovalsApi.createApproval(payload);
      return handleApiPromise(ApprovalOperation.REQUEST, promise);
    }
  }, [isChirpEnabled, chirpCreate, approvalType, approvalAction, approvableObjectId, assetName, approvalObjectIds, handleApiPromise]);

  // ========= approve =========
  const approve = useCallback(note => {
    if (isChirpEnabled) {
      return chirpApprove(note);
    }
    const promise = productApprovalsApi.approveApproval(resolvedData, note);
    return handleApiPromise(ApprovalOperation.APPROVE, promise);
  }, [isChirpEnabled, chirpApprove, resolvedData, handleApiPromise]);

  // ========= cancel =========
  const cancel = useCallback(note => {
    if (isChirpEnabled) {
      return chirpCancel(note);
    }
    const promise = productApprovalsApi.cancelApproval(resolvedData, note);
    return handleApiPromise(ApprovalOperation.CANCEL, promise);
  }, [isChirpEnabled, chirpCancel, resolvedData, handleApiPromise]);

  // ========= reject =========
  const reject = useCallback(note => {
    if (isChirpEnabled) {
      return chirpReject(note);
    }
    const promise = productApprovalsApi.rejectApproval(resolvedData, note);
    return handleApiPromise(ApprovalOperation.REJECT, promise);
  }, [isChirpEnabled, chirpReject, resolvedData, handleApiPromise]);

  // ========= update =========
  const update = useCallback(({
    approverOwnerIds,
    dueDateTimestamp
  }) => {
    if (isChirpEnabled) {
      return chirpUpdate({
        approverOwnerIds,
        dueDateTimestamp
      });
    }
    return productApprovalsApi.updateApproval(resolvedData, approverOwnerIds, dueDateTimestamp).then(response => {
      setData(response);
      return response;
    }).catch(errorResponse => {
      reportRequestException('updateApproval', errorResponse);
      throw errorResponse;
    });
  }, [isChirpEnabled, chirpUpdate, resolvedData]);
  const api = useMemo(() => ({
    fetchLatest: isChirpEnabled ? chirpFetchLatest : fetchLatest,
    create,
    cancel,
    reject,
    approve,
    update
  }), [isChirpEnabled, chirpFetchLatest, fetchLatest, create, cancel, reject, approve, update]);
  return {
    data: resolvedData,
    isLoading: isChirpEnabled ? chirpLoading || approverUsersLoading : isLoading,
    api,
    fetchError,
    hasFetchApproverUsersError: Boolean(fetchApproverUsersError)
  };
};